import React from 'react';

import { MediaElement, MediaText } from 'editor/src/store/design/types';

import useTr from 'editor/src/util/useTr';

import SliderWithInput from 'editor/src/component/SliderWithInput';

import useSliderChangeHandlers from './useSliderChangeHandlers';

// fabric's _fontSizeMult: a full line is fontSize × lineHeight × 1.13
const FONT_SIZE_MULT = 1.13;
const MAX_SPACING_PT = 72;

interface Props {
  element: MediaText;
  liveUpdate: (element: MediaElement) => void;
}

/**
 * Paragraph and section spacing of the selected text, in points. An unset spacing shows the
 * height of the full empty line it renders as, so the slider starts where the text is.
 */
function BlankLineSpacingControl({ element, liveUpdate }: Props) {
  const tr = useTr();
  const fullLinePt = Math.round(element.extra.fontSize * (element.extra.lineHeight ?? 1) * FONT_SIZE_MULT * 10) / 10;

  const paragraph = useSliderChangeHandlers(element, 'paragraphSpacing', liveUpdate);
  const section = useSliderChangeHandlers(element, 'sectionSpacing', liveUpdate);

  return (
    <>
      <div className="mt-3 mb-3">
        <SliderWithInput
          inputClassName="cy-paragraph-spacing-input"
          Label={tr('editor-text-paragraph-spacing', 'Paragraph spacing (pt)')}
          onChange={paragraph.onChange}
          onAfterChange={paragraph.onChanged}
          min={0}
          max={Math.max(MAX_SPACING_PT, fullLinePt)}
          step={0.5}
          startPoint={0}
          value={paragraph.value ?? fullLinePt}
          position="twoRows"
        />
      </div>
      <SliderWithInput
        inputClassName="cy-section-spacing-input"
        Label={tr('editor-text-section-spacing', 'Section spacing (pt)')}
        onChange={section.onChange}
        onAfterChange={section.onChanged}
        min={0}
        max={Math.max(MAX_SPACING_PT, fullLinePt * 2)}
        step={0.5}
        startPoint={0}
        value={section.value ?? fullLinePt * 2}
        position="twoRows"
      />
    </>
  );
}

export default React.memo(BlankLineSpacingControl);
